import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/Layout";
import Countdown from "@/components/Countdown";
import { Medal, Trophy, CheckCircle2, Clock, Package } from "lucide-react";
import api from "@/lib/api";
import { fmtDay } from "@/lib/format";

const MEDALS = { 1: "text-gold-300", 2: "text-slate-300", 3: "text-amber-600" };
const STATUS = { da_assegnare: "Da assegnare", assegnato: "Assegnato", consegnato: "Consegnato" };

export default function Prizes() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/prizes").then(({ data }) => setData(data)); }, []);

  if (!data) return <div className="min-h-[60vh] grid place-items-center text-muted-foreground">Caricamento…</div>;
  const { current_period, current_prizes, provisional_podium, winners } = data;

  return (
    <div>
      <PageHeader overline="Montepremi ogni 30 giorni" title="Premi del Mese"
        subtitle="Al termine di ogni periodo di 30 giorni, il podio della classifica vince premi esclusivi firmati The King of Resell." />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-12">
        {current_period && (
          <div className="surface-elevated p-6 text-center">
            <div className="overline-label mb-2">Periodo corrente</div>
            <h2 className="font-heading text-2xl font-bold text-white">{current_period.name}</h2>
            <p className="text-sm text-muted-foreground mt-1">{fmtDay(current_period.start_date)} → {fmtDay(current_period.end_date)}</p>
            <div className="mt-5 flex justify-center"><Countdown target={current_period.end_date} /></div>
            <p className="text-xs text-muted-foreground mt-3">Tempo rimanente alla chiusura del periodo</p>
          </div>
        )}

        <div>
          <h3 className="font-heading text-2xl font-extrabold uppercase text-white mb-5 text-center">Premi in Palio</h3>
          <div className="grid md:grid-cols-3 gap-5">
            {(current_prizes || []).map((p) => (
              <div key={p.position} className={`surface overflow-hidden ${p.position === 1 ? "border-gold-300/40 shadow-goldsm" : ""}`} data-testid={`prize-${p.position}`}>
                {p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-48 object-cover" />}
                <div className="p-5">
                  <div className="flex items-center gap-2">
                    <Medal className={MEDALS[p.position]} size={24} />
                    <span className="font-heading text-xl font-bold text-white uppercase">{p.title}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{p.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {provisional_podium?.length > 0 && (
          <div>
            <h3 className="font-heading text-2xl font-extrabold uppercase text-white mb-5 text-center">Podio Provvisorio</h3>
            <div className="surface divide-y divide-gold-300/5">
              {provisional_podium.map((r) => (
                <div key={r.user_id} className="flex items-center justify-between px-5 py-3">
                  <div className="flex items-center gap-3">
                    <Medal size={20} className={MEDALS[r.position]} />
                    <span className="font-semibold text-white">{r.nickname}</span>
                  </div>
                  <span className="font-heading font-black text-gold-300 text-lg">{r.points} pt</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3 text-center flex items-center justify-center gap-1"><Clock size={12} /> Classifica provvisoria: il vincitore sarà confermato alla chiusura del periodo.</p>
          </div>
        )}

        <div>
          <h3 className="font-heading text-2xl font-extrabold uppercase text-white mb-5 text-center">Albo dei Vincitori</h3>
          {winners?.length ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {winners.map((w, i) => (
                <div key={i} className="surface p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Medal size={22} className={MEDALS[w.position]} />
                    <div>
                      <div className="font-semibold text-white">{w.nickname}</div>
                      <div className="text-xs text-muted-foreground">{w.period?.name || "—"}</div>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full inline-flex items-center gap-1 ${w.status === "consegnato" ? "bg-status-correct/15 text-status-correct" : "bg-ink-700 text-slate-300"}`}>
                    {w.status === "consegnato" ? <CheckCircle2 size={12} /> : <Package size={12} />}{STATUS[w.status] || w.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="surface p-8 text-center text-muted-foreground"><Trophy className="mx-auto mb-2 text-ink-600" size={32} />Nessun vincitore registrato finora. Il primo potresti essere tu!</div>
          )}
        </div>
      </div>
    </div>
  );
}
